import { useState, useEffect } from "react";
import { Car, Users, FileSignature, Receipt, Clock, Send, Loader2, TrendingUp, Banknote, CreditCard, Wallet, CalendarClock, FileSpreadsheet, AlertTriangle, FileCheck2, ShieldCheck, TrendingDown, FileText } from "lucide-react";
import { apiFetch, downloadFile } from "@/portal/api";
import { fmtCZK } from "@/portal/components/adminShared";
import { toast } from "@/components/ui/sonner";
import { confirmDialog } from "@/portal/confirm";
import { useAuth } from "@/portal/AuthContext";
import FinanceOverviewCard from "@/portal/components/FinanceOverviewCard";

const StatCard = ({ icon: Icon, label, value, accent, testid }) => (
  <div className="glass rounded-2xl p-5" data-testid={testid}>
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${accent}`}>
      <Icon className="w-5 h-5" />
    </div>
    <p className="text-2xl font-bold font-['Cabinet_Grotesk']">{value}</p>
    <p className="text-xs text-zinc-500 mt-0.5">{label}</p>
  </div>
);

const DashboardTab = ({ clients, cars, token, onGoClients }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const today = new Date().toISOString().slice(0, 10);
  const [runningReminders, setRunningReminders] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [reportMonth, setReportMonth] = useState(new Date().toISOString().slice(0, 7));

  const downloadReport = async () => {
    try {
      await downloadFile(`/admin/reports/monthly.pdf?month=${reportMonth}`, token, `report-${reportMonth}.pdf`);
      toast.success("Měsíční report stažen.");
    } catch (e) { toast.error(e.message); }
  };
  const [expenses, setExpenses] = useState([]);
  useEffect(() => {
    if (!isAdmin) return;
    apiFetch("/admin/expenses", { token }).then(setExpenses).catch(() => {});
  }, [isAdmin, token]);

  const carExpiry = (d) => {
    if (!d) return null;
    const days = Math.round((new Date(d + "T00:00:00") - new Date()) / 86400000);
    return days < 0 ? "expired" : days <= 30 ? "soon" : "ok";
  };
  const expiringCars = (cars || []).filter((c) =>
    ["expired", "soon"].includes(carExpiry(c.stk_date)) || ["expired", "soon"].includes(carExpiry(c.insurance_date)));
  const fmtD = (s) => (s ? new Date(s + "T00:00:00").toLocaleDateString("cs-CZ", { day: "numeric", month: "short", year: "numeric" }) : "—");

  const runReminders = async () => {
    const ok = await confirmDialog({
      title: "Odeslat e-mailové upomínky?",
      description: "Systém zkontroluje všechny faktury a odešle předupomínky před splatností i upomínky po splatnosti.",
      confirmText: "Odeslat upomínky",
    });
    if (!ok) return;
    setRunningReminders(true);
    try {
      const r = await apiFetch("/admin/reminders/run", { method: "POST", token });
      toast.success(`Hotovo. Zkontrolováno: ${r.checked}, předupomínek: ${r.pre_due_sent}, po splatnosti: ${r.overdue_sent}, zaplaceno (přeskočeno): ${r.skipped_paid}, chyb: ${r.errors}.`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setRunningReminders(false);
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      await downloadFile("/admin/invoices/export.csv", token, `zaplacene-faktury-${today}.csv`);
      toast.success("CSV export stažen.");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setExporting(false);
    }
  };

  const rentals = clients.flatMap((c) => c.rentals || []);
  const contracts = clients.flatMap((c) => (c.contracts || []).map((ct) => ({ ...ct, clientName: c.name })));
  const invoices = clients.flatMap((c) => (c.invoices || []).map((i) => ({ ...i, clientName: c.name })));

  // Smlouvy končící do 30 dnů
  const in30 = new Date(); in30.setDate(in30.getDate() + 30);
  const in30Key = in30.toISOString().slice(0, 10);
  const endingSoon = clients
    .flatMap((c) => (c.rentals || []).map((r) => ({ ...r, clientName: c.name })))
    .filter((r) => r.date_to && r.date_to >= today && r.date_to <= in30Key)
    .sort((a, b) => (a.date_to < b.date_to ? -1 : 1));

  const awaiting = contracts.filter((c) => c.status !== "signed");
  const isUnpaid = (i) => i.status !== "paid" && i.status !== "cancelled";
  const isOverdue = (i) => isUnpaid(i) && (i.status === "overdue" || (i.due_on && i.due_on < today));
  const unpaid = invoices.filter(isUnpaid);
  const overdue = invoices.filter(isOverdue);
  const issuedTotal = invoices.reduce((s, i) => s + (Number(i.amount) || 0), 0);

  // --- Účetnictví: tržby podle způsobu úhrady ---
  const paidInvoices = invoices.filter((i) => i.status === "paid");
  const sumBy = (arr, pred) => arr.filter(pred).reduce((s, i) => s + (Number(i.amount) || 0), 0);
  const cashTotal = sumBy(paidInvoices, (i) => i.payment_method === "cash");
  const transferTotal = sumBy(paidInvoices, (i) => i.payment_method === "transfer");
  const otherTotal = sumBy(paidInvoices, (i) => !i.payment_method);
  const paidTotal = cashTotal + transferTotal + otherTotal;
  const expensesTotal = (expenses || []).reduce((s, e) => s + (Number(e.amount) || 0), 0);
  const profit = paidTotal - expensesTotal;

  const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  const nowD = new Date();
  const months = [];
  for (let k = 5; k >= 0; k--) {
    const d = new Date(nowD.getFullYear(), nowD.getMonth() - k, 1);
    months.push({ key: monthKey(d), label: d.toLocaleDateString("cs-CZ", { month: "short", year: "2-digit" }) });
  }
  const byMonth = months.map((m) => {
    const inM = paidInvoices.filter((i) => i.paid_on && monthKey(new Date(i.paid_on)) === m.key);
    return {
      ...m,
      cash: sumBy(inM, (i) => i.payment_method === "cash"),
      transfer: sumBy(inM, (i) => i.payment_method === "transfer"),
      other: sumBy(inM, (i) => !i.payment_method),
    };
  });
  const maxMonth = Math.max(1, ...byMonth.map((m) => m.cash + m.transfer + m.other));
  const pct = (v) => `${Math.round((v / maxMonth) * 100)}%`;

  return (
    <div data-testid="dashboard-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Car} label="Vozů v číselníku" value={cars.length} accent="bg-indigo-500/20 text-indigo-400" testid="stat-cars" />
        <StatCard icon={Users} label="Klientů" value={clients.length} accent="bg-sky-500/20 text-sky-400" testid="stat-clients" />
        <StatCard icon={FileSignature} label="Smluv k podpisu" value={awaiting.length} accent="bg-amber-500/20 text-amber-400" testid="stat-awaiting" />
        <StatCard icon={Receipt} label="Faktur nezaplaceno" value={unpaid.length} accent="bg-emerald-500/20 text-emerald-400" testid="stat-unpaid" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upomínky */}
        {isAdmin && (
        <div className="glass rounded-2xl p-6" data-testid="reminders-card">
          <div className="flex items-start justify-between gap-3 mb-1">
            <h3 className="font-['Cabinet_Grotesk'] text-lg font-bold flex items-center gap-2">
              <Clock className="w-5 h-5 text-red-400" /> Upomínky – po splatnosti
            </h3>
            <button
              onClick={runReminders}
              disabled={runningReminders}
              className="shrink-0 text-xs font-medium px-3 py-1.5 rounded-full bg-red-500/20 text-red-300 hover:bg-red-500/30 inline-flex items-center gap-1.5 disabled:opacity-60"
              data-testid="run-reminders-btn"
              title="Zkontrolovat faktury a odeslat e-mailové upomínky"
            >
              {runningReminders ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Poslat upomínky
            </button>
          </div>
          <p className="text-xs text-zinc-500 mb-4">
            Systém kontroluje faktury <strong className="text-zinc-400">automaticky každý den</strong>: připomene splatnost {`${5}`} dní předem a po splatnosti posílá upomínky (po 2 dnech navíc s pokutou 500 Kč/den).
          </p>
          {overdue.length === 0 ? (
            <p className="text-sm text-zinc-500 py-4 text-center">Žádné faktury po splatnosti 🎉</p>
          ) : (
            <div className="space-y-2">
              {overdue.map((i) => (
                <div key={i.id} className="flex items-center justify-between text-sm" data-testid={`reminder-${i.id}`}>
                  <span className="text-zinc-300">{i.clientName} · {i.invoice_type === "deposit" ? "kauce" : "nájemné"}</span>
                  <span className="text-red-400">{fmtCZK(i.amount)} · spl. {i.due_on || "—"}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Smlouvy k podpisu */}
        <div className="glass rounded-2xl p-6" data-testid="awaiting-card">
          <h3 className="font-['Cabinet_Grotesk'] text-lg font-bold mb-1 flex items-center gap-2">
            <FileSignature className="w-5 h-5 text-amber-400" /> Čeká na podpis klienta
          </h3>
          <p className="text-xs text-zinc-500 mb-4">Smlouvy, které klient ještě nepodepsal.</p>
          {awaiting.length === 0 ? (
            <p className="text-sm text-zinc-500 py-4 text-center">Vše podepsáno ✅</p>
          ) : (
            <div className="space-y-2">
              {awaiting.map((c) => (
                <div key={c.id} className="flex items-center justify-between text-sm" data-testid={`awaiting-${c.id}`}>
                  <span className="text-zinc-300">{c.clientName}</span>
                  <span className="text-zinc-500">{c.car_label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 glass rounded-2xl p-6" data-testid="vehicle-alerts-card">
        <h3 className="font-['Cabinet_Grotesk'] text-lg font-bold mb-1 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-400" /> STK / pojistka – brzy končí nebo propadlé
        </h3>
        <p className="text-xs text-zinc-500 mb-4">Vozidla, kterým končí technická kontrola nebo pojistka do 30 dnů (nebo už propadly).</p>
        {expiringCars.length === 0 ? (
          <p className="text-sm text-zinc-500 py-4 text-center">Vše v pořádku – žádné blížící se konce. ✅</p>
        ) : (
          <div className="space-y-2" data-testid="vehicle-alerts-list">
            {expiringCars.map((c) => {
              const stk = carExpiry(c.stk_date);
              const ins = carExpiry(c.insurance_date);
              return (
                <div key={c.id} className="flex items-center justify-between gap-3 text-sm" data-testid={`vehicle-alert-${c.id}`}>
                  <span className="text-zinc-300">{c.brand} {c.model}{c.license_plate ? ` (${c.license_plate})` : ""}</span>
                  <span className="flex flex-wrap gap-x-3 text-[11px]">
                    {["expired", "soon"].includes(stk) && <span className={`inline-flex items-center gap-1 ${stk === "expired" ? "text-red-400" : "text-amber-400"}`}><FileCheck2 className="w-3 h-3" /> STK {fmtD(c.stk_date)}</span>}
                    {["expired", "soon"].includes(ins) && <span className={`inline-flex items-center gap-1 ${ins === "expired" ? "text-red-400" : "text-amber-400"}`}><ShieldCheck className="w-3 h-3" /> Pojistka {fmtD(c.insurance_date)}</span>}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-6 glass rounded-2xl p-6" data-testid="ending-soon-card">
        <h3 className="font-['Cabinet_Grotesk'] text-lg font-bold mb-1 flex items-center gap-2">
          <CalendarClock className="w-5 h-5 text-orange-400" /> Smlouvy končící do 30 dnů
        </h3>
        <p className="text-xs text-zinc-500 mb-4">Pronájmy, jejichž datum konce nastává během následujících 30 dnů – ideální čas oslovit klienta s prodloužením.</p>
        {endingSoon.length === 0 ? (
          <p className="text-sm text-zinc-500 py-4 text-center">Žádné smlouvy nekončí v nejbližších 30 dnech.</p>
        ) : (
          <div className="space-y-2" data-testid="ending-soon-list">
            {endingSoon.map((r) => (
              <div key={r.id} className="flex items-center justify-between text-sm" data-testid={`ending-soon-${r.id}`}>
                <span className="text-zinc-300">{r.clientName}{r.car ? ` · ${r.car.brand} ${r.car.model}` : ""}</span>
                <span className="text-orange-300">konec {r.date_to}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {isAdmin && (<>
      <FinanceOverviewCard invoices={invoices} token={token} onGoClients={onGoClients} />

      <div className="mt-6 glass rounded-2xl p-6" data-testid="accounting-card">
        <div className="flex items-center justify-between gap-3 mb-1">
          <div className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-emerald-400" />
            <h3 className="font-['Cabinet_Grotesk'] text-lg font-bold">Účetnictví – tržby podle úhrady</h3>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <input type="month" value={reportMonth} onChange={(e) => setReportMonth(e.target.value)} className="input-dark text-xs py-1.5 px-2 w-auto" data-testid="report-month" />
            <button onClick={downloadReport} className="shrink-0 text-xs font-medium px-3 py-1.5 rounded-full bg-orange-500/20 text-orange-300 hover:bg-orange-500/30 inline-flex items-center gap-1.5" data-testid="report-pdf-btn" title="Stáhnout měsíční report (tržby vs. náklady + vytíženost)">
              <FileText className="w-3.5 h-3.5" /> Měsíční report (PDF)
            </button>
            <button
              onClick={exportCsv}
              disabled={exporting}
              className="shrink-0 text-xs font-medium px-3 py-1.5 rounded-full bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 inline-flex items-center gap-1.5 disabled:opacity-60"
              data-testid="export-csv-btn"
              title="Stáhnout zaplacené faktury jako CSV (Excel)"
            >
              {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />}
              Export CSV
            </button>
          </div>
        </div>
        <p className="text-xs text-zinc-500 mb-5">Zaplacené faktury rozdělené podle způsobu úhrady (posledních 6 měsíců dle data úhrady).</p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          <div className="rounded-xl bg-green-500/10 border border-green-500/20 p-4" data-testid="acc-cash-total">
            <div className="flex items-center gap-2 text-green-300 text-xs font-medium mb-1"><Banknote className="w-4 h-4" /> Hotově</div>
            <p className="text-xl font-bold font-['Cabinet_Grotesk'] text-green-300">{fmtCZK(cashTotal)}</p>
          </div>
          <div className="rounded-xl bg-sky-500/10 border border-sky-500/20 p-4" data-testid="acc-transfer-total">
            <div className="flex items-center gap-2 text-sky-300 text-xs font-medium mb-1"><CreditCard className="w-4 h-4" /> Převodem</div>
            <p className="text-xl font-bold font-['Cabinet_Grotesk'] text-sky-300">{fmtCZK(transferTotal)}</p>
          </div>
          <div className="rounded-xl bg-white/5 border border-white/10 p-4" data-testid="acc-paid-total">
            <div className="flex items-center gap-2 text-zinc-300 text-xs font-medium mb-1"><Receipt className="w-4 h-4" /> Zaplaceno celkem</div>
            <p className="text-xl font-bold font-['Cabinet_Grotesk']">{fmtCZK(paidTotal)}</p>
            {otherTotal > 0 && <p className="text-[11px] text-zinc-500 mt-0.5">z toho neurčeno: {fmtCZK(otherTotal)}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
          <div className="rounded-xl bg-red-500/10 border border-red-500/20 p-4" data-testid="acc-expenses-total">
            <div className="flex items-center gap-2 text-red-300 text-xs font-medium mb-1"><TrendingDown className="w-4 h-4" /> Náklady celkem</div>
            <p className="text-xl font-bold font-['Cabinet_Grotesk'] text-red-300">{fmtCZK(expensesTotal)}</p>
          </div>
          <div className={`rounded-xl border p-4 ${profit >= 0 ? "bg-emerald-500/10 border-emerald-500/20" : "bg-red-500/10 border-red-500/20"}`} data-testid="acc-profit-total">
            <div className={`flex items-center gap-2 text-xs font-medium mb-1 ${profit >= 0 ? "text-emerald-300" : "text-red-300"}`}><TrendingUp className="w-4 h-4" /> Zisk (tržby − náklady)</div>
            <p className={`text-xl font-bold font-['Cabinet_Grotesk'] ${profit >= 0 ? "text-emerald-300" : "text-red-300"}`}>{fmtCZK(profit)}</p>
          </div>
        </div>

        {paidTotal === 0 ? (
          <p className="text-sm text-zinc-500 py-4 text-center">Zatím žádné zaplacené faktury.</p>
        ) : (
          <div className="space-y-2.5" data-testid="acc-monthly">
            <div className="flex items-center gap-4 text-[11px] text-zinc-500 mb-1">
              <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-green-500" /> hotově</span>
              <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-sky-500" /> převodem</span>
              {otherTotal > 0 && <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-zinc-500" /> neurčeno</span>}
            </div>
            {byMonth.map((m) => {
              const tot = m.cash + m.transfer + m.other;
              return (
                <div key={m.key} className="flex items-center gap-3" data-testid={`acc-month-${m.key}`}>
                  <span className="w-16 text-xs text-zinc-400 shrink-0">{m.label}</span>
                  <div className="flex-1 h-5 rounded-md bg-white/5 overflow-hidden flex">
                    {m.cash > 0 && <div className="h-full bg-green-500/80" style={{ width: pct(m.cash) }} title={`Hotově ${fmtCZK(m.cash)}`} />}
                    {m.transfer > 0 && <div className="h-full bg-sky-500/80" style={{ width: pct(m.transfer) }} title={`Převodem ${fmtCZK(m.transfer)}`} />}
                    {m.other > 0 && <div className="h-full bg-zinc-500/70" style={{ width: pct(m.other) }} title={`Neurčeno ${fmtCZK(m.other)}`} />}
                  </div>
                  <span className="w-24 text-right text-xs font-medium text-zinc-300 shrink-0">{fmtCZK(tot)}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-6 glass rounded-2xl p-5 flex items-center justify-between" data-testid="issued-total-card">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center"><TrendingUp className="w-5 h-5" /></div>
          <div>
            <p className="text-xs text-zinc-500">Vystaveno celkem ({invoices.length} faktur)</p>
            <p className="text-xl font-bold font-['Cabinet_Grotesk']">{fmtCZK(issuedTotal)}</p>
          </div>
        </div>
        <button onClick={onGoClients} className="text-sm text-indigo-400 hover:text-indigo-300" data-testid="go-clients-btn">Spravovat klienty →</button>
      </div>
      </>)}
    </div>
  );
};

export default DashboardTab;
